<!-- dashboard/routes/dashboard/settings/account/components/auto-resolve/ConfigForm.vue -->
<script setup>
import { ref, computed, watch } from 'vue';
import TextArea from 'next/textarea/TextArea.vue';
import Switch from 'next/switch/Switch.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import SingleSelect from 'dashboard/components-next/filter/inputs/SingleSelect.vue';
import multiselect from 'vue-multiselect';

const props = defineProps({
  type: {
    type: String,
    required: true,
    validator: (v) => ['label', 'agent', 'team'].includes(v),
  },
  isEditing: {
    type: Boolean,
    default: false,
  },
  editingItem: {
    type: Object,
    default: null,
  },
  initialData: {
    type: Object,
    default: null,
  },
  selectOptions: {
    type: Array,
    default: () => [],
  },
  labelOptions: {
    type: Array,
    default: () => [],
  },
  durationOptions: {
    type: Array,
    default: () => [],
  },
  isSubmitting: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['submit', 'cancel']);

const selectedItem = ref({});
const duration = ref(10);
const unit = ref('minutes');
const message = ref('');
const labelToApply = ref({});
const ignoreWaiting = ref(false);

const typeLabels = {
  label: { select: 'Etiqueta', placeholder: 'Selecione uma etiqueta' },
  agent: { select: 'Agente', placeholder: 'Selecione um agente' },
  team: { select: 'Time', placeholder: 'Selecione um time' },
};

const formTitle = computed(() => {
  return props.isEditing ? 'Editar configuração' : 'Nova configuração';
});

const selectedUnit = computed({
  get: () => props.durationOptions.find((o) => o.value === unit.value) || null,
  set: (opt) => {
    unit.value = opt?.value ?? 'minutes';
  },
});

watch(
  () => props.initialData,
  (data) => {
    if (data) {
      selectedItem.value = props.editingItem || {};
      duration.value = data.duration;
      unit.value = data.unit;
      message.value = data.message;
      labelToApply.value = data.labelToApply || {};
      ignoreWaiting.value = data.ignoreWaiting;
    }
  },
  { immediate: true }
);

watch(
  () => props.isEditing,
  (editing) => {
    if (!editing && !props.initialData) {
      resetForm();
    }
  }
);

const resetForm = () => {
  selectedItem.value = {};
  duration.value = 10;
  unit.value = 'minutes';
  message.value = '';
  labelToApply.value = {};
  ignoreWaiting.value = false;
};

const handleSubmit = () => {
  emit('submit', {
    selectedItem: selectedItem.value,
    duration: duration.value,
    unit: unit.value,
    message: message.value,
    labelToApply: labelToApply.value,
    ignoreWaiting: ignoreWaiting.value,
  });
};

const handleCancel = () => {
  resetForm();
  emit('cancel');
};
</script>

<template>
  <div class="bg-n-solid-2 rounded-xl p-5 border border-n-weak">
    <div class="flex justify-between items-center mb-5">
      <h4 class="text-sm font-medium text-n-slate-12">
        {{ formTitle }}
      </h4>
      <button
        @click="handleCancel"
        class="p-1 text-n-slate-10 hover:text-n-slate-12 rounded-md hover:bg-n-alpha-1 transition-colors"
      >
        <span class="i-lucide-x w-4 h-4" />
      </button>
    </div>

    <form class="space-y-4" @submit.prevent="handleSubmit">
      <div v-if="!isEditing">
        <label class="block text-sm text-n-slate-11 mb-1.5">
          {{ typeLabels[type].select }}
        </label>
        <SingleSelect
          v-model="selectedItem"
          :options="selectOptions"
          :placeholder="typeLabels[type].placeholder"
          variant="faded"
        />
      </div>

      <div v-else class="flex items-center gap-2 p-3 bg-n-alpha-1 rounded-lg">
        <span
          v-if="type === 'label' && editingItem?.color"
          class="w-3 h-3 rounded"
          :style="{ backgroundColor: editingItem.color }"
        />
        <span class="text-sm font-medium text-n-slate-12">{{ editingItem?.name }}</span>
        <span v-if="editingItem?.email" class="text-xs text-n-slate-10">
          {{ editingItem.email }}
        </span>
      </div>

      <div>
        <label class="block text-sm text-n-slate-11 mb-1.5">
          Tempo de inatividade
        </label>
        <div class="grid grid-cols-[1fr_140px] gap-2">
          <input
            v-model.number="duration"
            type="number"
            min="1"
            max="1000000"
            class="w-full h-9 px-3 text-sm bg-n-alpha-black2 border-0 outline outline-1 outline-n-weak rounded-lg text-n-slate-12 placeholder:text-n-slate-10 hover:outline-n-slate-6 focus:outline-n-brand-ink transition-colors"
            placeholder="10"
          />
          <multiselect
            v-model="selectedUnit"
            class="multiselect-sm !mb-0 w-full text-sm"
            :options="durationOptions"
            track-by="value"
            label="label"
            :allow-empty="false"
            :show-labels="false"
            :searchable="false"
            placeholder="Unidade"
          />
        </div>
      </div>

      <div>
        <label class="block text-sm text-n-slate-11 mb-1.5">
          Mensagem de encerramento
          <span class="text-n-slate-9">(opcional)</span>
        </label>
        <TextArea
          v-model="message"
          class="w-full"
          rows="2"
          placeholder="Mensagem enviada ao cliente quando a conversa for resolvida"
        />
      </div>

      <div class="space-y-3 pt-2 border-t border-n-weak">
        <div class="flex items-center justify-between pt-3">
          <label class="text-sm text-n-slate-11">
            Ignorar conversas aguardando resposta
          </label>
          <Switch v-model="ignoreWaiting" />
        </div>
        
        <div class="flex items-center justify-between">
          <label class="text-sm text-n-slate-11">
            Aplicar etiqueta após resolver
          </label>
          <SingleSelect
            v-model="labelToApply"
            :options="labelOptions"
            placeholder="Nenhuma"
            placeholder-icon="i-lucide-chevron-down"
            placeholder-trailing-icon
            variant="faded"
          />
        </div>
      </div>

      <div class="flex justify-end gap-2 pt-4">
        <NextButton
          slate
          sm
          type="button"
          @click="handleCancel"
          label="Cancelar"
        />
        <NextButton
          blue
          sm
          type="submit"
          :is-loading="isSubmitting"
          :label="isEditing ? 'Salvar' : 'Adicionar'"
        />
      </div>
    </form>
  </div>
</template>